import { areThemesValid, parseThemeNumbers } from './themeUtils';

export interface ThemeValidationResult {
    isValid: boolean;
    statusMessage?: string;
}

/**
 * Validates existing storefront ID field
 * Requires themes to be provided if storefront ID is present
 */
export const validateExistingStorefrontId = (
    value: string,
    themes: string
): ThemeValidationResult => {
    const hasExistingStorefrontId = !!value.trim();
    const hasThemes = !!themes.trim();

    // If an existing storefront ID is provided, then themes are required for the form to be valid.
    if (hasExistingStorefrontId && !hasThemes) {
        return {
            isValid: false,
            statusMessage: "Themes versions are required when importing from an existing storefront.",
        };
    }

    return { isValid: true };
};

/**
 * Validates themes field
 * Requires existing storefront ID to be provided if themes are present
 */
export const validateThemes = (
    value: string,
    existingStorefrontId: string
): ThemeValidationResult => {
    const hasThemes = !!value.trim();
    const hasExistingStorefrontId = !!existingStorefrontId.trim();

    // If themes are provided, an existing storefront ID is required for the form to be valid.
    if (hasThemes && !hasExistingStorefrontId) {
        return {
            isValid: false,
            statusMessage: "Existing Store ID required for themes to be imported.",
        };
    }

    // Validate theme numbers format if both fields are provided
    if (hasThemes && hasExistingStorefrontId) {
        const themeNumbers = parseThemeNumbers(value);
        if (themeNumbers.length === 0) {
            return {
                isValid: false,
                statusMessage: "Valid theme versions are required when importing themes from an existing storefront.",
            };
        }
    }

    return { isValid: true };
};

/**
 * Validates the overall theme import form
 * Ensures both fields are either empty or both have values
 */
export const validateThemeImportForm = (
    existingStorefrontId: string,
    themes: string
): ThemeValidationResult => {
    const hasExistingStorefrontId = !!existingStorefrontId.trim();
    const hasThemes = !!themes.trim();

    // Both fields should be empty, or both should have values
    const valid = (!hasExistingStorefrontId && !hasThemes) || (hasExistingStorefrontId && hasThemes);

    return {
        isValid: valid,
        statusMessage: valid ? "" : "Both Store ID and themes are required for theme import.",
    };
};
